import { getAccessToken } from '@/lib/auth/authSession'
import { attemptTokenRefresh } from '@/lib/auth/tokenRefresh'
import { performLogout } from '@/lib/auth/performLogout'
import { markApiDown, probeApiReachable } from '@/lib/api/apiHealth'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

let loggingOut = false

const withAuthHeader = (init: RequestInit | undefined, token: string | null): RequestInit => {
  const headers = new Headers(init?.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (!headers.has('Content-Type') && init?.body) headers.set('Content-Type', 'application/json')
  return { ...init, headers }
}

/**
 * `fetch` wrapper that shares the 401 handling contract with AuthAxios:
 *   1. First 401 → attempt token refresh, retry once with new token.
 *   2. Retry still 401 → probe API; if reachable, logout + redirect to /auth.
 *   3. API unreachable → surface the error, keep the session (offline mode).
 *
 * Non-401 errors are returned as-is — the caller decides how to handle them.
 * Safe to call concurrently: the refresh itself is deduped in tokenRefresh.ts.
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(input, withAuthHeader(init, getAccessToken()))

  if (response.status !== 401) return response

  const url = typeof input === 'string' ? input : input.toString()
  logger.logEvent(LogCategory.AUTH_SESSION, 'sync.401.attempting_refresh', { url })

  const refreshed = await attemptTokenRefresh()

  if (refreshed) {
    const retryResponse = await fetch(input, withAuthHeader(init, getAccessToken()))
    if (retryResponse.status !== 401) return retryResponse
    logger.logEvent(LogCategory.AUTH_SESSION, 'sync.401.retry_still_unauthorized', { url })
  }

  // Refresh failed or retry still 401. If the API is actually down, hold the
  // session so offline-first screens keep working.
  if (!(await probeApiReachable())) {
    markApiDown()
    logger.logEvent(LogCategory.AUTH_SESSION, 'sync.refresh.failed.api_unreachable_hold_session', { url })
    return response
  }

  // Guard against multiple in-flight sync requests each trying to log out.
  if (!loggingOut) {
    loggingOut = true
    logger.logEvent(LogCategory.AUTH_SESSION, 'sync.expired.forcing_logout', { url })
    await performLogout()
    window.location.href = '/auth'
  }

  return response
}
